import React from 'react'

import { useForTablet } from '@sketch/components'

import { useCanvasScope } from '../../../canvas/scope'
import { DraftAnnotationNotifier } from '../../../annotations/components'
import { SharePageCanvas } from '../../PageCanvasView/components/SharePageCanvas'
import { PageCanvasSupportGate } from '../../PageCanvasView/components/PageCanvasSupportGate'
import { StyledCanvasViewWrapper } from '../../PageCanvasView/PageCanvasView.styles'

interface CanvasSurfaceProps {
  canvasRef?: React.RefObject<HTMLDivElement | null>
  /**
   * Rendered beside the canvas, before it - the left panel, in every view that
   * wants one. Nothing here is required: a view that has no use for a panel
   * simply passes no children.
   */
  children?: React.ReactNode
}

/**
 * The canvas, and whatever a view chooses to put beside it.
 *
 * Identical across the page, Section and frame views and across both hosts,
 * because everything that varied between them is read rather than passed: the
 * page to render and the entity to isolate both come from the scope.
 *
 * The browser-support check lives here rather than in each view. It was written
 * for the page canvas and copied into every view built from it, which is why
 * the frame view - the one with a different ancestry - never had it and showed
 * a silently broken canvas where the others explained.
 */
export function CanvasSurface({ canvasRef, children }: CanvasSurfaceProps) {
  const scope = useCanvasScope()
  const isTabletAndBigger = useForTablet()

  if (!scope) {
    return null
  }

  return (
    <PageCanvasSupportGate>
      <StyledCanvasViewWrapper>
        {children}
        <SharePageCanvas
          ref={canvasRef}
          permanentPageIdentifier={scope.pageUUID}
          // The focused entity - frame or Section - becomes the renderer's
          // scene root, which isolates it and scopes the canvas overlays to
          // what it contains: a Section keeps the overlays of its child
          // entities so you can drill into it, while a frame contains none and
          // shows no overlays.
          rootEntityUUID={scope.entityUUID}
        />
        {isTabletAndBigger && <DraftAnnotationNotifier />}
      </StyledCanvasViewWrapper>
    </PageCanvasSupportGate>
  )
}
